import { useSuspenseQuery } from "@tanstack/react-query";
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState
} from "react";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { addRedirectListener, getOidcUser } from "./userManager";
import { LoggedInUserDetails } from "@/backend/user/authTypes";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import { client } from "@/frontend/@api/trpcClient";
import { useCurrentOrPreviousNonFalsey } from "@/frontend/@tools/useCurrentOrPreviousNonFalsey";
import { LoadingBoundaryFallback } from "@/frontend/@ui/LoadingBoundary";

interface LoggedInUserContextType {
    userId: string;
    firmIds: string[];
    isSuper: boolean;
}

const LoggedInUserContext = createContext<LoggedInUserContextType>({
    userId: "",
    firmIds: [],
    isSuper: false
});

const LOGGED_IN_USER_DETAILS_KEY = "auth/loggedInUserDetails";

const getInitialData = (userEmail: string) => {
    if (!userEmail) {
        return null;
    }
    const storedData = localStorage.getItem(LOGGED_IN_USER_DETAILS_KEY);
    if (storedData) {
        try {
            const data = JSON.parse(storedData) as LoggedInUserDetails;
            if (data.email.toLowerCase() === userEmail.toLowerCase()) {
                return data;
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Error parsing stored logged in user details", error);
        }
    }
    return undefined;
};

export const deleteLoggedInUserDetails = () => {
    localStorage.removeItem(LOGGED_IN_USER_DETAILS_KEY);
};

export const LoggedInUserProvider = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate();
    const auth = useAuth();
    const { isAuthenticated, isLoading, activeNavigator, user } = auth;

    // use last successful authenticated email address
    // (if user gets logged out, it is better to show old content rather than auto-redirecting to login.
    // the user may be in the middle of editing content and is good to give them the chance to copy and paste before being logged out)
    const authenticatedEmail = isAuthenticated
        ? (user?.profile.email ?? undefined)
        : undefined;
    const previousEmail = useCurrentOrPreviousNonFalsey(authenticatedEmail);
    const userEmail = authenticatedEmail ?? previousEmail ?? "";

    // logged in user details
    const { data } = useSuspenseQuery({
        queryKey: [LOGGED_IN_USER_DETAILS_KEY, userEmail],
        queryFn: () =>
            userEmail ? client.auth.getLoggedInUserDetails.query() : null,
        initialData: getInitialData(userEmail),
        initialDataUpdatedAt: () => Date.now() - 1000 * 60 * 60 // make sure we immediately update the data by using a timestamp in the past
    });

    // store user details in local storage to avoid a waterfall step on initial app load
    useEffect(() => {
        if (data) {
            localStorage.setItem(
                LOGGED_IN_USER_DETAILS_KEY,
                JSON.stringify(data)
            );
        }
    }, [data]);

    // auto login based on https://github.com/authts/react-oidc-context/blob/main/src/useAutoSignin.ts
    const [hasTriedSignin, setHasTriedSignin] = useState(false);
    const shouldAttemptSignin =
        !hasAuthParams() &&
        !activeNavigator &&
        !userEmail &&
        !hasTriedSignin &&
        !isLoading;
    useEffect(() => {
        void (async () => {
            if (shouldAttemptSignin) {
                let user = getOidcUser();
                if (user) {
                    // if user exists, a silent signin may be possible if the refresh token is valid
                    user = await auth.signinSilent();
                }
                const isSignedIn =
                    user?.expires_at && user.expires_at > Date.now() / 1000;
                if (!isSignedIn) {
                    await auth.signinRedirect({
                        url_state: "REDIRECT_HASH=" + window.location.hash
                    });
                }
                setHasTriedSignin(true);
            }
        })();
    }, [auth, shouldAttemptSignin]);

    // listen for redirect hash
    useEffect(() => {
        return addRedirectListener(hash => {
            void navigate(hash, { replace: true });
        });
    }, [navigate]);

    // while authentication is initially loading / auto signin is happening, we want to show a spinner (but we don't have a promise to trigger suspense)
    if (!userEmail && (isLoading || !hasTriedSignin)) {
        return <LoadingBoundaryFallback absolute={true} delayType="long" />;
    }

    // not authenticated - ideally shouldn't get this far, but best to show a login button if we do
    if (!userEmail) {
        return <LoginPage />;
    }

    // context value
    const contextValue: LoggedInUserContextType =
        data && data.email.toLowerCase() === userEmail.toLowerCase()
            ? {
                  userId: data.id,
                  firmIds: data.firmIds,
                  isSuper: data.isSuper
              }
            : {
                  userId: "",
                  firmIds: [],
                  isSuper: false
              };

    return (
        <LoggedInUserContext.Provider value={contextValue}>
            {children}
        </LoggedInUserContext.Provider>
    );
};

export const useLoggedInUser = () => {
    return useContext(LoggedInUserContext);
};

const LoginPage = () => {
    const auth = useAuth();

    return (
        <div className="flex h-screen items-center justify-center p-6">
            <Card className="mx-auto mt-8 w-full max-w-md">
                <CardHeader>
                    <CardTitle>Login</CardTitle>
                    <CardDescription>Please continue to login</CardDescription>
                </CardHeader>
                <CardContent>
                    <Button
                        onClick={() => {
                            void auth.signinRedirect();
                        }}
                    >
                        Login
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
};
